import { forwardRef, useId, useState, type ChangeEvent } from 'react';
import { SparkIcon } from '../SparkIcon';
import { Label } from '../Label/Label';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import { useControllableState } from '../../hooks/useControllableState';
import type { InputPasswordProps } from './InputPassword.types';
import '../Input/Input.css';
import './InputPassword.css';

export const InputPassword = forwardRef<HTMLInputElement, InputPasswordProps>(function InputPassword({
  label = 'Label',
  helperText = 'Helper text',
  placeholder = 'Password',
  size = 'medium',
  fill,
  showLabel = true,
  showAsterisk = false,
  showHelpIcon = false,
  showHelperText = false,
  showPrefixIcon = false,
  prefixIconSlug = 'lock-password-icon',
  disabled = false,
  visualState,
  showFocus = false,
  className = '',
  value: valueProp,
  defaultValue = '',
  onChange,
  onKeyDown,
}, ref) {
  const inputId = useId();
  const [value, setValue] = useControllableState(valueProp, defaultValue);
  const [visible, setVisible] = useState(false);
  const resolvedState = disabled ? 'disabled' : visualState ?? 'default';
  const isFocused = showFocus || resolvedState === 'click-on';
  const resolvedFill = fill ?? (value.length > 0 ? 'filled' : 'empty');

  const rootClasses = [
    'spark-input',
    'spark-input-password',
    `spark-input-${size}`,
    `spark-input-fill-${resolvedFill}`,
    `spark-input-state-${resolvedState}`,
    disabled ? 'spark-input-disabled' : '',
    isFocused ? 'spark-input-show-focus' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    onChange?.(event);
  };

  return (
    <div className={rootClasses}>
      {showLabel ? (
        <Label
          label={label}
          htmlFor={inputId}
          showAsterisk={showAsterisk}
          showIcon={showHelpIcon}
        />
      ) : null}
      <div className="spark-input-field-wrap">
        <div className="spark-input-anatomy spark-input-password-field">
          {showPrefixIcon ? <SparkIcon slug={prefixIconSlug} size="small" className="spark-input-prefix-icon" /> : null}
          <input
            ref={ref}
            id={inputId}
            className="spark-input-control"
            type={visible ? 'text' : 'password'}
            placeholder={placeholder}
            disabled={disabled}
            value={value}
            onChange={handleChange}
            onKeyDown={onKeyDown}
          />
          <TooltipTrigger text={visible ? 'Hide password' : 'Show password'} disabled={disabled}>
            <button
              type="button"
              className="spark-input-password-toggle"
              aria-label={visible ? 'Hide password' : 'Show password'}
              disabled={disabled}
              onClick={() => setVisible((current) => !current)}
            >
              <SparkIcon slug={visible ? 'eye-closed' : 'eye'} size="small" />
            </button>
          </TooltipTrigger>
        </div>
        <span className="spark-input-focus" aria-hidden="true" />
      </div>
      {showHelperText ? <span className="spark-input-helper">{helperText}</span> : null}
    </div>
  );
});

InputPassword.displayName = 'InputPassword';
